import { dateRangeOperations } from '@ardoq/date-range';
import {
  FieldAnswerValue,
  DateRange,
  ReferenceAnswerValue,
  AttributeAnswerValue,
  TagAnswerValue,
} from '../types';
import {
  PersistedConditionFieldQuestion,
  PersistedParentQuestion,
  PersistedAttributeQuestion,
  PersistedFieldQuestion,
  PersistedHiddenQuestion,
  PersistedReferenceQuestion,
  PersistedSurveyQuestion,
  PersistedTagQuestion,
  PersistedTextQuestion,
  SurveyQuestionType,
  SurveyQuestionValidator,
  PersistedReferenceSubQuestion,
  PersistedFieldSubQuestion,
} from '@ardoq/api-types';

export const isRangeValidator = (validator: SurveyQuestionValidator) =>
  validator.type === 'range';

export const isParentQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedParentQuestion =>
  isAttributeQuestion(question) &&
  question.properties.attributeName === 'parent';

export const isConditionQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedConditionFieldQuestion =>
  question.type === SurveyQuestionType.FIELD &&
  Boolean(question.hasConditionals);

export const isReferenceQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedReferenceQuestion =>
  question.type === SurveyQuestionType.REFERENCE;

export const isReferenceQuestionWithSubQuestions = (
  question: PersistedSurveyQuestion
): question is PersistedReferenceQuestion & {
  properties: { questions: PersistedReferenceSubQuestion[] };
} =>
  Boolean(
    isReferenceQuestion(question) &&
    question.properties.questions &&
    question.properties.questions.length > 0
  );

export const isFieldQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedFieldQuestion =>
  question.type === SurveyQuestionType.FIELD;

export const isAttributeQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedAttributeQuestion =>
  question.type === SurveyQuestionType.ATTRIBUTE;

export const isTagQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedTagQuestion => question.type === SurveyQuestionType.TAG;

export const isDateRangeQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedFieldQuestion =>
  isFieldQuestion(question) &&
  dateRangeOperations.isCombinedDateRangeFieldNames(
    question.properties.fieldName
  );

export const isDateRangeValue = (
  value?:
    | FieldAnswerValue
    | ReferenceAnswerValue[]
    | TagAnswerValue
    | AttributeAnswerValue
): value is DateRange | undefined => {
  if (value === undefined) return true;
  return (
    value !== null &&
    typeof value === 'object' &&
    ('startDate' in value || 'endDate' in value)
  );
};

export const isTextQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedTextQuestion =>
  question.type === SurveyQuestionType.TEXT;

export const isHiddenQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedHiddenQuestion =>
  question.type === SurveyQuestionType.HIDDEN_FIELD;

export const isAnswerableQuestion = (question: PersistedSurveyQuestion) => {
  if (isTextQuestion(question)) return false;
  if (isHiddenQuestion(question)) return true;
  return !question.readonly;
};

export const containsSubQuestions = (
  question: PersistedSurveyQuestion
): question is PersistedConditionFieldQuestion | PersistedReferenceQuestion => {
  if (isReferenceQuestion(question)) {
    return Boolean(
      question.properties.questions && question.properties.questions.length > 0
    );
  }
  if (isConditionQuestion(question)) {
    return (
      question.properties.questions && question.properties.questions.length > 0
    );
  }
  return false;
};

export const isPersistedFieldSubQuestion = (
  question: PersistedSurveyQuestion
): question is PersistedFieldSubQuestion =>
  'displayConditions' in question.properties;
